import React from 'react';
import { Alert, Box, Button, Stack, Typography } from '@mui/material';
import { shellTokens } from '../shell/theme/shellTheme';
import FleetKpiStrip from './components/FleetKpiStrip';
import FleetMatrix from './components/FleetMatrix';
import PositionsLedger from './components/PositionsLedger';
import StrategyDeskDrawer from './components/StrategyDeskDrawer';
import { useFleet } from './hooks/useFleet';
import { useStrategyDeskStore } from './strategyDeskStore';

const toggleSx = (active) => ({
    border: `1px solid ${active ? 'rgba(37, 99, 235, 0.25)' : shellTokens.border}`,
    bgcolor: active ? 'rgba(37, 99, 235, 0.1)' : shellTokens.surface,
    borderRadius: '8px',
    px: 1.75,
    py: 0.85,
    fontSize: '0.78rem',
    fontWeight: 700,
    color: active ? shellTokens.primary : shellTokens.muted,
    textTransform: 'none',
    minWidth: 0,
});

const StrategyDeskPage = () => {
    const { data, error, isLoading, isFetching, refetch } = useFleet();
    const viewMode = useStrategyDeskStore((state) => state.viewMode);
    const modeFilter = useStrategyDeskStore((state) => state.modeFilter);
    const selectedInstanceId = useStrategyDeskStore((state) => state.selectedInstanceId);
    const drawerOpen = useStrategyDeskStore((state) => state.drawerOpen);
    const setViewMode = useStrategyDeskStore((state) => state.setViewMode);
    const setModeFilter = useStrategyDeskStore((state) => state.setModeFilter);
    const selectInstance = useStrategyDeskStore((state) => state.selectInstance);
    const clearSelection = useStrategyDeskStore((state) => state.clearSelection);
    const closeDrawer = useStrategyDeskStore((state) => state.closeDrawer);

    const instances = data?.instances || [];
    const positions = data?.positions || [];
    const summary = data?.summary;
    const selectedInstance = instances.find((item) => item.instanceId === selectedInstanceId) || null;

    return (
        <Box>
            <Stack
                direction={{ xs: 'column', md: 'row' }}
                spacing={1}
                justifyContent="space-between"
                alignItems={{ xs: 'stretch', md: 'center' }}
                sx={{ mb: 1.75 }}
            >
                <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                    <Button sx={toggleSx(viewMode === 'strategy')} onClick={() => setViewMode('strategy')}>
                        Strategy × Client
                    </Button>
                    <Button sx={toggleSx(viewMode === 'client')} onClick={() => setViewMode('client')}>
                        Client × Strategy
                    </Button>
                </Stack>

                <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap alignItems="center">
                    {['all', 'live', 'sim'].map((mode) => (
                        <Button
                            key={mode}
                            sx={toggleSx(modeFilter === mode)}
                            onClick={() => setModeFilter(mode)}
                        >
                            {mode === 'all' ? 'All modes' : mode}
                        </Button>
                    ))}
                    {selectedInstanceId ? (
                        <Button sx={toggleSx(false)} onClick={clearSelection}>
                            Clear selection
                        </Button>
                    ) : null}
                    {isFetching && !isLoading ? (
                        <Typography sx={{ fontSize: '0.72rem', color: shellTokens.muted, px: 0.5 }}>
                            Refreshing…
                        </Typography>
                    ) : null}
                </Stack>
            </Stack>

            {error ? (
                <Alert
                    severity="error"
                    sx={{ mb: 1.75 }}
                    action={
                        <Button color="inherit" size="small" onClick={() => refetch()}>
                            Retry
                        </Button>
                    }
                >
                    Failed to load fleet data: {error.message}
                </Alert>
            ) : null}

            <FleetKpiStrip summary={summary} loading={isLoading} />
            <FleetMatrix
                instances={instances}
                viewMode={viewMode}
                modeFilter={modeFilter}
                selectedInstanceId={selectedInstanceId}
                onSelectInstance={selectInstance}
                loading={isLoading}
            />
            <PositionsLedger
                positions={positions}
                instances={instances}
                selectedInstanceId={selectedInstanceId}
                loading={isLoading}
            />
            <StrategyDeskDrawer
                open={drawerOpen && Boolean(selectedInstance)}
                instance={selectedInstance}
                onClose={closeDrawer}
            />
        </Box>
    );
};

export default StrategyDeskPage;
